class CartDrawer extends HTMLElement {
  constructor() {
    super();
    window.FoxTheme = window.FoxTheme || {};
    window.FoxTheme.Cart = this;
    this.drawer = this.closest("drawer-component");
    window.FoxTheme.CartDrawer = this.drawer;
    this.openWhenAdded = this.dataset.openWhenAdded === "true";

    this.onCartRefreshListener = this.onCartRefresh.bind(this);
  }

  connectedCallback() {
    window.FoxThemeEvents.subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      async (state) => {
        if (state.item_count !== 0) {
          this.renderContents(
            state,
            true,
            state.source === "quick-order-list" ? false : true
          );
        }
      }
    );

    document.addEventListener("cart:refresh", this.onCartRefreshListener);
  }

  disconnectedCallback() {
    document.removeEventListener("cart:refresh", this.onCartRefreshListener);
  }

  async onCartRefresh(event) {
    const cartDrawer = document.getElementById("cart-drawer");
    if (!cartDrawer) return;

    try {
      const response = await fetch(
        `${FoxThemeSettings.routes.root}?section_id=cart-drawer`
      );
      const responseText = await response.text();

      const parser = new DOMParser();
      const parsedHTML = parser.parseFromString(responseText, "text/html");

      const newCartContent = parsedHTML.getElementById("cart-drawer").innerHTML;
      cartDrawer.innerHTML = newCartContent;

      if (event.detail.open === true && !this.drawer.isOpen()) {
        this.drawer.openDrawer();
      }
    } catch (error) {
      console.error("Error refreshing cart:", error);
    }
  }

  renderContents(parsedState, renderFooter = true, isOpen = true) {
    // No section HTML (e.g. the Early Access page's intercepted add-to-cart reply): nothing to redraw.
    if (parsedState.errors || !parsedState.sections) return;
    this.classList.contains("is-empty") && this.classList.remove("is-empty");
    this.getSectionsToRender(renderFooter).forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    setTimeout(() => {
      if (this.openWhenAdded && !this.drawer.isOpen() && isOpen) {
        this.drawer.openDrawer();
        document.body.classList.add("prevent-scroll");
        window.FoxThemeEvents.emit(`ON_CART_DRAWER_OPEN`, parsedState);
      }
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender(renderFooter = false) {
    const sections = [
      {
        id: "cart-drawer",
        selector: ".f-cart-drawer__items",
      },
      {
        id: "cart-drawer",
        selector: ".f-cart-drawer__samples",
      },
      {
        id: "cart-drawer",
        selector: ".f-cart-drawer__upsells",
      },
    ];

    if (renderFooter) {
      sections.push({
        id: "cart-drawer",
        selector: ".f-drawer__footer",
      });
    }

    return sections;
  }
}
customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  initLoading() {
    this.loading = new window.FoxTheme.AnimateLoading(
      document.querySelector("cart-drawer"),
      { overlay: document.querySelector("cart-drawer") }
    );
  }
  getSectionsToRender() {
    return [
      {
        id: "Drawer-Cart",
        section: "cart-drawer",
        selector: ".f-cart-drawer__items",
      },
      {
        id: "Drawer-Cart",
        section: "cart-drawer",
        selector: ".f-cart-drawer__block-subtotal",
      },
      {
        id: "Drawer-Cart",
        section: "cart-drawer",
        selector: ".f-cart-drawer__samples",
      },
      {
        id: "Drawer-Cart",
        section: "cart-drawer",
        selector: ".f-cart-drawer__upsells",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);

// Shared carousel for the drawer's sample picker and upsell list. A custom
// element so it re-arms itself when cart-drawer.js swaps a block's innerHTML
// after a cart update. Scroll-snap does the carousel; this only adds arrows
// and mouse dragging on top of it.
class CartCarousel extends HTMLElement {
  // Ids of collapsible carousels the customer has opened. Lives outside the
  // element because cart-drawer.js replaces it on every cart update, and a
  // picker that snaps shut after each add would be unusable.
  static opened = new Set();

  connectedCallback() {
    this.track = this.querySelector("[data-carousel-track]");
    this.nav = this.querySelector("[data-carousel-nav]");
    if (!this.track) return;

    const details = this.querySelector("details");
    if (details && this.id) {
      if (CartCarousel.opened.has(this.id)) details.open = true;
      details.addEventListener("toggle", () => {
        if (details.open) CartCarousel.opened.add(this.id);
        else CartCarousel.opened.delete(this.id);
      });
    }

    this.querySelector("[data-carousel-prev]")?.addEventListener("click", () =>
      this.step(-1)
    );
    this.querySelector("[data-carousel-next]")?.addEventListener("click", () =>
      this.step(1)
    );

    this.track.addEventListener("scroll", () => this.syncArrows(), {
      passive: true,
    });
    this.track.addEventListener("dragstart", (event) => event.preventDefault());
    this.track.addEventListener("pointerdown", this.onPointerDown.bind(this));
    this.track.addEventListener("pointermove", this.onPointerMove.bind(this));
    this.track.addEventListener("pointerup", this.onPointerUp.bind(this));
    this.track.addEventListener("pointercancel", this.onPointerUp.bind(this));
    // A drag that ends over a card must not also fire its add button.
    this.track.addEventListener(
      "click",
      (event) => {
        if (!this.dragged) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    this.syncArrows();
    // The drawer is hidden on load, so widths are only real once it opens.
    this.observer = new ResizeObserver(() => this.syncArrows());
    this.observer.observe(this.track);
  }

  disconnectedCallback() {
    // cart-drawer.js replaces this block's innerHTML on every cart update, so
    // the observer has to go with the element it was watching.
    this.observer?.disconnect();
    clearTimeout(this.settleTimer);
  }

  // Distance from one card to the next, gap included, measured rather than
  // assumed — the sample picker and the upsell list use different gaps.
  stepSize() {
    const [first, second] = this.track.children;
    if (first && second) return second.offsetLeft - first.offsetLeft;
    return first ? first.offsetWidth : this.track.clientWidth;
  }

  step(direction) {
    this.track.scrollBy({
      left: this.stepSize() * direction,
      behavior: "smooth",
    });
  }

  syncArrows() {
    const overflow = this.track.scrollWidth - this.track.clientWidth;
    if (this.nav) this.nav.hidden = overflow < 2;
    const prev = this.querySelector("[data-carousel-prev]");
    const next = this.querySelector("[data-carousel-next]");
    if (prev) prev.disabled = this.track.scrollLeft < 2;
    if (next) next.disabled = this.track.scrollLeft >= overflow - 2;
  }

  onPointerDown(event) {
    // Touch already scrolls natively, and a press on a control is a click.
    // Links are draggable: upsell cards are wrapped in one, and the capture
    // handler above swallows the click when the press turned into a drag.
    if (event.pointerType === "touch" || event.button !== 0) return;
    if (event.target.closest("button, input, select, textarea")) return;

    this.dragging = true;
    this.dragged = false;
    this.startX = event.clientX;
    this.startScroll = this.track.scrollLeft;
    // No pointer capture yet: capturing here would retarget the click to the
    // track, so a plain click on an upsell card would never reach its link.
  }

  onPointerMove(event) {
    if (!this.dragging) return;
    const delta = event.clientX - this.startX;
    if (!this.dragged && Math.abs(delta) > 4) {
      this.dragged = true;
      // Now it is a drag, not a click: hold the pointer so it keeps working
      // outside the drawer, and drop the snap points until it is released.
      this.track.setPointerCapture(event.pointerId);
      this.track.classList.add("is-dragging");
    }
    if (!this.dragged) return;
    this.flick = event.clientX - (this.lastX ?? event.clientX);
    this.lastX = event.clientX;
    this.track.scrollLeft = this.startScroll - delta;
  }

  onPointerUp(event) {
    if (!this.dragging) return;
    this.dragging = false;
    if (this.track.hasPointerCapture(event.pointerId)) {
      this.track.releasePointerCapture(event.pointerId);
    }
    if (this.dragged) this.settle();
    else this.track.classList.remove("is-dragging");
    this.lastX = null;
    setTimeout(() => (this.dragged = false));
  }

  // A released drag glides to a card the way a swipe does. Rounding to the
  // nearest card would drop a short drag back where it started, which on a
  // 300px card means most drags feel like nothing happened — so a nudge past a
  // small threshold, or any flick, advances exactly one card.
  settle() {
    const step = this.stepSize();
    const moved = this.track.scrollLeft - this.startScroll;
    const from = Math.round(this.startScroll / step);
    const threshold = Math.min(50, step * 0.12);
    const flick = this.flick ?? 0;

    let index = from;
    if (Math.abs(moved) >= step) {
      index = Math.round(this.track.scrollLeft / step); // dragged past a card
    } else if (moved > threshold || flick < -2) {
      index = from + 1;
    } else if (moved < -threshold || flick > 2) {
      index = from - 1;
    }

    const last = this.track.children.length - 1;
    index = Math.max(0, Math.min(index, last));
    this.track.scrollTo({ left: index * step, behavior: "smooth" });

    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(
      () => this.track.classList.remove("is-dragging"),
      400
    );
  }
}
if (!customElements.get("cart-carousel")) {
  customElements.define("cart-carousel", CartCarousel);
}

// Removes free samples once no paid product is left, so a sample can never
// check out on its own. snippets/cart-sample-cleanup.liquid renders this only
// in that state, so it costs nothing on a normal cart.
class CartSampleCleanup extends HTMLElement {
  // The drawer and the cart page can both render one on /cart.
  static running = false;

  async connectedCallback() {
    const keys = (this.dataset.keys || "").split(",").filter(Boolean);
    if (!keys.length || CartSampleCleanup.running) return;
    CartSampleCleanup.running = true;

    // Same path cart.js takes after a quantity change: one request that returns
    // the updated sections, then each cart component re-renders its own parts.
    // (The theme's cart:refresh event would swap the whole <drawer-component>,
    // which ships hidden, and shut the drawer on the customer.)
    const components = [
      ...document.querySelectorAll("cart-items, cart-drawer-items"),
    ];
    const sections = [
      ...new Set(
        components.flatMap((component) =>
          component.getSectionsToRender().map((section) => section.section)
        )
      ),
    ];

    try {
      const response = await fetch(
        `${FoxThemeSettings.routes.cart_update_url}.js`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            updates: Object.fromEntries(keys.map((key) => [key, 0])),
            sections,
            sections_url: window.location.pathname,
          }),
        }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const state = await response.json();

      const isEmpty = state.item_count === 0;
      const emptyTargets = [
        document.querySelector("cart-drawer"),
        document.getElementById("main-cart-footer"),
        ...components,
      ];
      for (const element of emptyTargets) {
        element?.classList.toggle("is-empty", isEmpty);
      }
      for (const component of components) component.renderContents(state);
      window.FoxThemeEvents.emit(PUB_SUB_EVENTS.cartUpdate, state);
    } catch (error) {
      console.error("Free sample cleanup failed:", error);
    } finally {
      CartSampleCleanup.running = false;
    }
  }
}
if (!customElements.get("cart-sample-cleanup")) {
  customElements.define("cart-sample-cleanup", CartSampleCleanup);
}
